import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export const useThemeStore = defineStore('theme', () => {
  // State - ensure these are declared first
  const theme = ref('system')
  const isInitialized = ref(false)
  const mediaQuery = ref(null)
  const cleanup = ref(null)

  // Computed properties
  const isDark = computed(() => {
    if (typeof window === 'undefined') return false
    
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme.value === 'dark'
  })

  const currentTheme = computed(() => {
    return isDark.value ? 'dark' : 'light'
  })

  const themeIcon = computed(() => {
    switch (theme.value) {
      case 'light':
        return 'fas fa-sun'
      case 'dark':
        return 'fas fa-moon'
      case 'system':
        return 'fas fa-circle-half-stroke'
      default:
        return 'fas fa-circle-half-stroke'
    }
  })

  const themeLabel = computed(() => {
    switch (theme.value) {
      case 'light':
        return 'Light Mode'
      case 'dark':
        return 'Dark Mode'
      case 'system':
        return 'System Default'
      default:
        return 'System Default'
    }
  })

  // Actions
  const setTheme = (newTheme) => {
    if (['light', 'dark', 'system'].includes(newTheme)) {
      theme.value = newTheme
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('igw-theme', newTheme)
      }
      applyTheme()
    }
  }

  const toggleTheme = () => {
    switch (theme.value) {
      case 'light':
        setTheme('dark')
        break
      case 'dark':
        setTheme('system')
        break
      case 'system':
        setTheme('light')
        break
      default:
        setTheme('light')
    }
  }

  const applyTheme = () => {
    if (typeof document === 'undefined') return
    
    const htmlElement = document.documentElement
    
    if (isDark.value) {
      htmlElement.classList.add('dark')
    } else {
      htmlElement.classList.remove('dark')
    }
    
    // Update meta theme color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark.value ? '#0f172a' : '#ffffff')
    }
  }

  const initialize = () => {
    if (isInitialized.value) return
    
    try {
      // Get saved theme from localStorage
      const savedTheme = typeof localStorage !== 'undefined' ? localStorage.getItem('igw-theme') : null
      
      if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
        theme.value = savedTheme
      } else {
        // Default to system preference
        theme.value = 'system'
      }

      // Apply initial theme
      applyTheme()

      // Listen for system theme changes
      if (typeof window !== 'undefined') {
        mediaQuery.value = window.matchMedia('(prefers-color-scheme: dark)')
        
        const handleSystemThemeChange = () => {
          if (theme.value === 'system') {
            applyTheme()
          }
        }

        mediaQuery.value.addEventListener('change', handleSystemThemeChange)

        // Store cleanup function
        cleanup.value = () => {
          if (mediaQuery.value) {
            mediaQuery.value.removeEventListener('change', handleSystemThemeChange)
          }
        }
      }

      // Watch for theme changes
      watch(isDark, () => {
        applyTheme()
      }, { immediate: true })

      isInitialized.value = true
    } catch (error) {
      console.error('Error initializing theme store:', error)
      // Set default theme even if initialization fails
      theme.value = 'system'
      isInitialized.value = true
    }
  }

  const getThemeOptions = () => [
    {
      value: 'light',
      label: 'Light Mode',
      icon: 'fas fa-sun',
      description: 'Always use light theme'
    },
    {
      value: 'dark',
      label: 'Dark Mode',
      icon: 'fas fa-moon',
      description: 'Always use dark theme'
    },
    {
      value: 'system',
      label: 'System Default',
      icon: 'fas fa-circle-half-stroke',
      description: 'Follow system preference'
    }
  ]

  return {
    // State
    theme,
    isInitialized,
    
    // Computed
    isDark,
    currentTheme,
    themeIcon,
    themeLabel,
    
    // Actions
    setTheme,
    toggleTheme,
    initialize,
    getThemeOptions,
    applyTheme
  }
}) 